import React, { forwardRef } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, styled } from "@mui/material";

import { ButtonVariant } from "Constant/button";
import ClipLoader from "react-spinners/ClipLoader";

import StandartButton from "components/atoms/StandartButton";

import { COLORS } from "../../CreationModal.constants";
import {
    creationButtonSx,
    creationCancelButtonSx,
    StatusChip,
} from "../../CreationModal.styled";

/** Left / center / right; the outer columns share the width so the chip is truly centered. */
const Row = styled(Box)({
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    gap: 12,
});

const Left = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifySelf: "start",
});

const Center = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
});

const Right = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifySelf: "end",
    gap: 8,
});

/**
 * Footer for the creation modals, built on the app's `StandartButton`s like
 * the SignalWizard footer. Left: outlined Back (when `onBack` is set), else
 * an outlined secondary action (`onSecondary`, e.g. "Create new tracker"),
 * else Cancel; center: optional status chip (e.g. "Upload selected"); right:
 * the primary contained action (`submitIcon` renders before the label, e.g.
 * a brand logo for OAuth connects).
 */
const CreationFooter = forwardRef(
    (
        {
            onBack,
            backLabel,
            onSecondary,
            secondaryLabel,
            onCancel,
            cancelLabel = "Cancel",
            isCancelDisabled = false,
            statusLabel,
            onSubmit,
            submitLabel,
            submitIcon,
            submitEndIcon,
            isSubmitDisabled = false,
            isSubmitLoading = false,
            submitTestId,
            hideSubmit = false,
        },
        ref,
    ) => {
        const renderLeft = () => {
            if (onBack) {
                return (
                    <StandartButton
                        variant={ButtonVariant.BLACK_OUTLINED}
                        onClick={onBack}
                        disabled={isCancelDisabled}
                        startIcon={<ArrowBackIcon />}
                        sx={creationButtonSx}
                    >
                        {backLabel}
                    </StandartButton>
                );
            }
            if (onSecondary) {
                return (
                    <StandartButton
                        variant={ButtonVariant.BLACK_OUTLINED}
                        onClick={onSecondary}
                        disabled={isCancelDisabled}
                        sx={creationButtonSx}
                    >
                        {secondaryLabel}
                    </StandartButton>
                );
            }
            if (onCancel) {
                return (
                    <StandartButton
                        variant={ButtonVariant.BLACK_OUTLINED}
                        color="inherit"
                        onClick={onCancel}
                        disabled={isCancelDisabled}
                        sx={creationCancelButtonSx}
                    >
                        {cancelLabel}
                    </StandartButton>
                );
            }
            return null;
        };

        return (
            <Row>
                <Left>{renderLeft()}</Left>
                <Center>
                    {statusLabel && (
                        <StatusChip>
                            <span className="status-dot" />
                            <span className="status-text">{statusLabel}</span>
                        </StatusChip>
                    )}
                </Center>
                <Right>
                    {!hideSubmit && (
                        <StandartButton
                            setRef={ref}
                            variant="contained"
                            color="primary"
                            onClick={onSubmit}
                            disabled={isSubmitDisabled || isSubmitLoading}
                            startIcon={submitIcon}
                            endIcon={
                                isSubmitLoading ? (
                                    <ClipLoader
                                        color={COLORS.white}
                                        size={14}
                                    />
                                ) : (
                                    submitEndIcon
                                )
                            }
                            sx={creationButtonSx}
                            data-cy={submitTestId}
                        >
                            {submitLabel}
                        </StandartButton>
                    )}
                </Right>
            </Row>
        );
    },
);

CreationFooter.displayName = "CreationFooter";

export default CreationFooter;
